import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notify } from '@/lib/notify';
import { AuthLayout } from '@/layouts/AuthLayout';
import { useAuth } from '@/contexts/AuthContext';
import { apiErrorMessage, isApiError } from '@/api/errors';
import { REMEMBER_KEY, LAST_EMAIL_KEY, readSignOut } from '@/config/session';
import { loginSchema, mfaSchema, recoveryCodeSchema, type LoginFormValues, type MfaFormValues } from '@/features/auth/schemas/login.schema';

function readRemembered(): { email: string; remember: boolean } {
  try {
    return { email: localStorage.getItem(LAST_EMAIL_KEY) ?? '', remember: localStorage.getItem(REMEMBER_KEY) === '1' };
  } catch {
    return { email: '', remember: false };
  }
}

/**
 * Login screen. Consumes the REAL auth endpoints via AuthContext. Handles direct
 * login + an MFA step, and honours "remember me" (prefills the last email and
 * asks the backend for a long-lived refresh session).
 */
export function LoginPage() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Return to the exact protected URL (path + query + hash) that required sign-in.
  const fromLoc = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
  const from = fromLoc?.pathname && fromLoc.pathname !== '/login'
    ? `${fromLoc.pathname}${fromLoc.search ?? ''}${fromLoc.hash ?? ''}`
    : '/';
  const remembered = readRemembered();

  const [mfa, setMfa] = useState<{ challengeId: string; remember: boolean } | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Explain an automatic sign-out (recorded by whichever tab timed out).
  const [signOutNotice] = useState(() => {
    const s = readSignOut();
    if (!s || s.reason !== 'idle' || Date.now() - s.at > 24 * 3_600_000) return null;
    const m = s.idleMinutes ?? 30;
    const span = m >= 60 && m % 60 === 0 ? `${m / 60} hour${m === 60 ? '' : 's'}` : `${m} minutes`;
    return `You were signed out after ${span} of inactivity. Sign in to continue.`;
  });

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: remembered.email, password: '', remember: remembered.remember },
  });
  const mfaForm = useForm<MfaFormValues>({ resolver: zodResolver(useRecovery ? recoveryCodeSchema : mfaSchema), defaultValues: { code: '' } });

  const onLogin = loginForm.handleSubmit(async (values) => {
    try {
      const res = await login({ email: values.email, password: values.password, remember: values.remember });
      if (res.mfaRequired && res.challengeId) {
        setMfa({ challengeId: res.challengeId, remember: !!values.remember });
        notify.info('Enter the verification code from your authenticator app.');
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      if (isApiError(err) && err.kind === 'unauthenticated') {
        loginForm.setError('password', { message: 'Incorrect email or password' });
      } else {
        notify.error(apiErrorMessage(err, 'Sign-in failed. Please try again.'));
      }
    }
  });

  const onMfa = mfaForm.handleSubmit(async (values) => {
    if (!mfa) return;
    try {
      await verifyMfa(useRecovery
        ? { challengeId: mfa.challengeId, recoveryCode: values.code }
        : { challengeId: mfa.challengeId, code: values.code });
      if (useRecovery) notify.info('You used a recovery code. Each code works once; regenerate them in Settings → Security when you can.');
      navigate(from, { replace: true });
    } catch (err) {
      if (isApiError(err) && err.code === 'AUTH_MFA_CHALLENGE_INVALID') {
        setMfa(null);
        setUseRecovery(false);
        mfaForm.reset();
        notify.error('That sign-in attempt expired. Sign in again.');
        return;
      }
      mfaForm.setError('code', { message: apiErrorMessage(err, 'That code is not valid. Check the time on your device and try again.') });
    }
  });

  if (mfa) {
    return (
      <AuthLayout title="Two-step verification"
        subtitle={useRecovery ? 'Enter one of the recovery codes you saved when you set this up.' : 'Enter the 6-digit code from your authenticator app.'}>
        <form onSubmit={onMfa} className="space-y-5" noValidate>
          <div>
            <label htmlFor="mfa-code" className="mb-2 block text-sm font-medium text-fg">{useRecovery ? 'Recovery code' : 'Verification code'}</label>
            <input id="mfa-code" key={useRecovery ? 'recovery' : 'totp'} {...mfaForm.register('code')}
              placeholder={useRecovery ? 'abcde-fghjk' : '123456'} inputMode={useRecovery ? 'text' : 'numeric'}
              autoComplete={useRecovery ? 'off' : 'one-time-code'} autoFocus spellCheck={false}
              className="h-12 w-full rounded-lg border border-border bg-surface px-4 text-base text-fg shadow-sm transition placeholder:text-fg-subtle focus:border-brand focus:ring-2 focus:ring-brand/30" />
            {mfaForm.formState.errors.code && <p className="mt-2 text-sm text-danger">{mfaForm.formState.errors.code.message}</p>}
          </div>
          <button type="submit" disabled={mfaForm.formState.isSubmitting}
            className="h-12 w-full rounded-lg bg-brand px-4 font-semibold text-brand-fg shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60">
            {mfaForm.formState.isSubmitting ? 'Verifying...' : 'Verify'}
          </button>
          <button type="button" className="h-11 w-full rounded-lg text-sm font-medium text-brand hover:bg-surface-raised"
            onClick={() => { setUseRecovery((v) => !v); mfaForm.reset(); }}>
            {useRecovery ? 'Use my authenticator app instead' : 'Lost your device? Use a recovery code'}
          </button>
          <button type="button" className="h-11 w-full rounded-lg text-sm font-medium text-fg-muted hover:bg-surface-raised hover:text-fg"
            onClick={() => { setMfa(null); setUseRecovery(false); mfaForm.reset(); }}>Back to sign in</button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Sign in" subtitle="Access your Nifty Hosting administration console.">
      {signOutNotice && (
        <p role="status" className="mb-5 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2 text-sm text-fg">{signOutNotice}</p>
      )}
      <form onSubmit={onLogin} className="space-y-5" noValidate>
        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-fg">Email</label>
          <input id="email" {...loginForm.register('email')} type="email" placeholder="you@company.com"
            autoComplete="username" autoFocus
            className="h-12 w-full rounded-lg border border-border bg-surface px-4 text-base text-fg shadow-sm transition placeholder:text-fg-subtle focus:border-brand focus:ring-2 focus:ring-brand/30" />
          {loginForm.formState.errors.email && <p className="mt-2 text-sm text-danger">{loginForm.formState.errors.email.message}</p>}
        </div>
        <div>
          <label htmlFor="password" className="mb-2 block text-sm font-medium text-fg">Password</label>
          <div className="relative">
            <input id="password" {...loginForm.register('password')} type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
              autoComplete="current-password"
              className="h-12 w-full rounded-lg border border-border bg-surface px-4 pr-16 text-base text-fg shadow-sm transition placeholder:text-fg-subtle focus:border-brand focus:ring-2 focus:ring-brand/30" />
            <button type="button" onClick={() => setShowPassword((value) => !value)}
              className="absolute inset-y-0 right-0 flex items-center px-4 text-sm font-medium text-fg-muted hover:text-fg"
              aria-label={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {loginForm.formState.errors.password && <p className="mt-2 text-sm text-danger">{loginForm.formState.errors.password.message}</p>}
        </div>
        <div className="flex items-center justify-between">
          <label className="flex cursor-pointer items-center gap-3 text-sm text-fg-muted">
            <input type="checkbox" checked={loginForm.watch('remember')}
              onChange={(event) => loginForm.setValue('remember', event.target.checked)}
              className="h-4 w-4 rounded border-border bg-surface text-brand accent-blue-500" />
            <span>Remember me</span>
          </label>
          <Link to="/reset-password" className="text-sm text-brand hover:underline">Forgot password?</Link>
        </div>
        <button type="submit" disabled={loginForm.formState.isSubmitting}
          className="h-12 w-full rounded-lg bg-brand px-4 font-semibold text-brand-fg shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60">
          {loginForm.formState.isSubmitting ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </AuthLayout>
  );
}

